import React, { useState, useEffect, FC } from 'react'
import { makeStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Pagination from '@mui/material/Pagination'
import Stack from '@mui/material/Stack'
import IconButton from '@mui/material/IconButton'
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import clsx from 'clsx'
import Scrollbar from '../Scrollbar'
import Divider from '@mui/material/Divider'
import Tooltip from '@mui/material/Tooltip'

type RenderColumnActionProps = {}
export const RenderColumnAction: FC<RenderColumnActionProps> = ({ children }) => {
    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                columnGap: '8px',
                width: 'inherit',
            }}
        >
            {children}
        </div>
    )
}

type Order = 'asc' | 'desc'
type DataTableProps<T> = {
    columnOptions: ColumnSettingType[]
    dataSource: T[]
    maxHeight?: number
    onRowClick?: (e: any) => void
    onPageNumber?: (page: number) => void
    onRowPerPage?: (rowPage: number) => void
    totalItem: number
    rowsPerPage: number
    onSort?: (sortField: string, sortType: string) => void
    sortField?: string
    sortType?: string
    pageNumber?: number
}
const DataTable = <T extends object = {}>(props: DataTableProps<T>) => {
    const classes = useStyles()
    const {
        columnOptions,
        dataSource,
        maxHeight = 300,
        onRowClick,
        onPageNumber,
        totalItem,
        rowsPerPage,
        onSort,
        sortField,
        sortType,
        pageNumber,
    } = props

    const [data, setData] = useState<any[]>([])
    const [order, setOrder] = useState<Order>('asc')
    const [sortActive, setSortActive] = useState({})
    const [page, setPage] = useState<number>(0)

    useEffect(() => {
        setData(dataSource)
        if (sortField && sortType) {
            setSortActive((prev) => ({
                ...prev,
                [sortField]: sortType,
            }))
        }
    }, [dataSource, sortType])

    useEffect(() => {
        setPage(pageNumber ?? 0)
    }, [pageNumber])

    const createSortHandler = (property: keyof any) => (event: React.MouseEvent<unknown>) => {
        const isAsc = sortActive[property] && order === 'asc'
        const sortField = property as string
        const sortType = isAsc ? 'desc' : 'asc'
        setOrder(sortType)
        setSortActive((prev) => ({
            ...prev,
            [sortField]: sortType,
        }))
        onSort?.(sortField, sortType)
    }

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage)
        onPageNumber?.(newPage)
    }

    function detectBrowser(): string {
        if ((navigator.userAgent.indexOf('Opera') || navigator.userAgent.indexOf('OPR')) != -1) {
            return '-webkit-fill-available'
        } else if (navigator.userAgent.indexOf('Chrome') != -1) {
            return '-webkit-fill-available'
        } else if (navigator.userAgent.indexOf('Safari') != -1) {
            return '-webkit-fill-available'
        } else if (navigator.userAgent.indexOf('Firefox') != -1) {
            return '-moz-available'
        } else {
            return '-webkit-fill-available'
        }
    }

    return (
        <>
            <div className={classes.root}>
                <Paper sx={{ width: '100%', height: '38px', bgcolor: '#F4F4F4' }}>
                    <Stack direction="row" sx={{ width: 'inherit', height: 'inherit' }}>
                        {columnOptions.map((column: ColumnSettingType, index) => {
                            const lenghtCol = columnOptions.length
                            switch (column.type) {
                                case 'action':
                                    return (
                                        <div
                                            key={index}
                                            style={{ display: 'flex', alignItems: 'center', width: '150px' }}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', width: 'inherit' }}>
                                                <Tooltip title={column?.label ?? ''} placement="bottom">
                                                    <Typography className={classes.headTypography}>
                                                        {column.label}
                                                    </Typography>
                                                </Tooltip>
                                                {lenghtCol !== index + 1 && (
                                                    <Divider sx={{ height: 18, m: 0.5 }} orientation="vertical" />
                                                )}
                                            </div>
                                        </div>
                                    )
                                default:
                                    return (
                                        <div
                                            key={index}
                                            className={clsx({ [classes.headOver]: props.onSort })}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                width: column?.width ? `${column.width}px` : detectBrowser(),
                                                minWidth: '100px',
                                            }}
                                            onClick={createSortHandler(column.dataKey)}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', width: 'inherit' }}>
                                                <Tooltip title={column?.label ?? ''} placement="bottom">
                                                    <Typography className={classes.headTypography}>
                                                        {column.label}
                                                    </Typography>
                                                </Tooltip>
                                                {props.onSort && (
                                                    <div>
                                                        {sortActive[column.dataKey] === 'asc' ? (
                                                            <ArrowDropUpIcon fontSize="medium" />
                                                        ) : (
                                                            <ArrowDropDownIcon fontSize="medium" />
                                                        )}
                                                    </div>
                                                )}

                                                {lenghtCol !== index + 1 && (
                                                    <Divider sx={{ height: 18, m: 0.5 }} orientation="vertical" />
                                                )}
                                            </div>
                                        </div>
                                    )
                            }
                        })}
                    </Stack>
                </Paper>

                {data.length === 0 && (
                    <Paper sx={{ my: 1, height: '70px' }}>
                        <div className={classes.dataRow}>ไม่พบข้อมูล</div>
                    </Paper>
                )}

                <Scrollbar
                    scrollbarTrackBorderRadius={10}
                    scrollbarThumbBorderRadius={10}
                    style={{ marginTop: '8px', marginBottom: '8px', maxHeight: `${props?.maxHeight ?? '300'}px` }}
                >
                    {data.map((row, rowIndex) => {
                        return (
                            <Paper key={rowIndex} sx={{ my: 1, minHeight: '70px', height: 'auto' }}>
                                <Stack direction="row" sx={{ minHeight: '70px' }}>
                                    {columnOptions.map((column: ColumnSettingType, index) => {
                                        const lenghtCol = columnOptions.length
                                        switch (column.type) {
                                            case 'action':
                                                return (
                                                    <div
                                                        key={index}
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            width: '150px',
                                                        }}
                                                    >
                                                        {column.render?.(row)}
                                                    </div>
                                                )
                                            default:
                                                return (
                                                    <div
                                                        key={index}
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            width: column?.width
                                                                ? `${column.width}px`
                                                                : detectBrowser(),
                                                            minWidth: '100px',
                                                        }}
                                                    >
                                                        <div
                                                            style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                width: 'inherit',
                                                            }}
                                                        >
                                                            <Typography
                                                                className={classes.rowTypography}
                                                                style={{ textAlign: column.align }}
                                                            >
                                                                {row[column.dataKey]}
                                                            </Typography>
                                                            {lenghtCol !== index + 1 && (
                                                                <Box sx={{ height: 18, m: 0.5 }} />
                                                                // <Divider sx={{ height: 18, m: 0.5 }} orientation="vertical" />
                                                            )}
                                                        </div>
                                                    </div>
                                                )
                                        }
                                    })}
                                </Stack>
                            </Paper>
                        )
                    })}
                </Scrollbar>
            </div>

            <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={2}>
                <Typography>
                    แสดงข้อมูล {data.length} จากทั้งหมด {totalItem} รายการ
                </Typography>
                <Pagination
                    count={isNaN(Math.ceil(totalItem / rowsPerPage)) ? 0 : Math.ceil(totalItem / rowsPerPage)}
                    page={page}
                    onChange={handleChangePage}
                    color="secondary"
                    showFirstButton
                    showLastButton
                />
            </Stack>
        </>
    )
}

export default DataTable

export type ColumnSettingType = {
    dataKey: string | 'action'
    label?: string
    width?: number
    align?: 'left' | 'center' | 'right' | 'justify' | 'inherit'
    type?: 'custom' | 'action'
    render?: (row: any) => JSX.Element
}

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'grid',
        width: '100%',
        overflowX: 'auto',
        overflowY: 'hidden',
        scrollbarGutter: 'stable',
        '&::-webkit-scrollbar:horizontal': {
            width: '6px',
        },
        '&::-webkit-scrollbar-track:horizontal': {
            borderRadius: '0px',
            backgroundColor: '#e4e4e4',
        },
        '&::-webkit-scrollbar-thumb:horizontal': {
            borderRadius: '0px',
            background: theme.palette.primary.main,
        },
        '&::-webkit-scrollbar-thumb:horizontal:hover': {
            background: theme.palette.primary.dark,
        },
    },
    headTypography: {
        width: '100%',
        textAlign: 'center',
        fontSize: '14px',
        color: '#000',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
    },
    dataRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: 'inherit',
    },
    rowTypography: {
        width: '100%',
        fontSize: '14px',
        color: '#0F192C',
    },
    headOver: {
        cursor: 'pointer',
    },
}))
